import { useEffect, useState } from 'react';
import {
  CommonButton,
  MainButton,
} from '../../../../../components/button/main';
import { ConsoleDrawer } from '../../../../../components/drawer';
import { FilterGroup } from '../../../../../components/filter-group';
import { CommonRadioButton } from '../../../../../components/radio/radio-button';
import { CommonSelect } from '../../../../../components/select';
import getFormatMessage from '../../../../../locales/get-format-message';
import styles from './index.module.scss';
import { NoticeSDKList } from './table';
import { getGameSdkVersion, getGCloudSdkList } from '../../../../../api';
import {
  IAPIGameSdkVersionOption,
  IApiGCloudGameItem,
  IGCloudSdkItem,
} from '../../../../../model/api-modules';
import { SdkSelect } from './sdk-select';
import { ReloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../../../store/reducers';
import { compareVersions, isValidEmail } from '../../../../../lib';

export enum FilterType {
  GameID = 'gameID',
  GcloudVersion = 'gcloudVersion',
  SdkVersion = 'sdkVersion',
}

export interface IFilter {
  type: FilterType;
  searchValues: string[] | IGCloudSdkItem[];
}

export interface ILastQuery {
  filter?: IFilter;
  result?: IApiGCloudGameItem[];
  allList?: IApiGCloudGameItem[];
}

interface IProps {
  onClose: () => void;
  onChange: (res: ILastQuery) => void;
}

export const ScopeDrawer = (props: IProps) => {
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const { onClose, onChange } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [filterOptionInfo, setFilterOptionInfo] =
    useState<IAPIGameSdkVersionOption>();

  const [filter, setFilter] = useState<IFilter>({
    type: FilterType.GameID,
    searchValues: [],
  });
  const [lastQuery, setLastQuery] = useState<ILastQuery>({});

  const [sdkList, setSdkList] = useState<readonly IApiGCloudGameItem[]>([]);
  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const res = await getGameSdkVersion();
    setFilterOptionInfo(res);
  };

  const renderSelect = () => {
    if (!filterOptionInfo) {
      return (
        <CommonSelect
          name="searchValues"
          options={[]}
          mode="multiple"
          placeholder={getFormatMessage('select')}
        />
      );
    }
    if (filter.type === FilterType.GameID) {
      const options = filterOptionInfo.gameIdList.map((gameId) => ({
        label: gameId,
        value: gameId,
      }));
      return (
        <CommonSelect
          name="searchValues"
          options={options}
          mode="multiple"
          placeholder={getFormatMessage('select')}
        />
      );
    }
    if (filter.type === FilterType.GcloudVersion) {
      const options = [...filterOptionInfo.gcloudVersionList]
        .sort(compareVersions)
        .map((version) => ({
          label: version,
          value: version,
        }));
      return (
        <CommonSelect
          name="searchValues"
          mode="multiple"
          options={options}
          placeholder={getFormatMessage('select')}
        />
      );
    }
    if (filter.type === FilterType.SdkVersion) {
      return (
        <SdkSelect name="searchValues" sdkInfo={filterOptionInfo.sdkList} />
      );
    }
  };

  const clearForm = () => {
    setFilter({ ...filter, searchValues: [] });
  };

  const search = async () => {
    if (!userInfo) {
      return;
    }

    const query: Record<string, any> = {};
    const { type, searchValues } = filter;
    if (type === FilterType.GameID) {
      query.gameIdStr = searchValues.join();
    }
    if (type === FilterType.GcloudVersion) {
      query.gcloudVersionStr = searchValues.join();
    }
    if (type === FilterType.SdkVersion) {
      query.sdkVersionList = searchValues as IGCloudSdkItem[];
    }
    setLoading(true);
    const res = await getGCloudSdkList({
      operator: userInfo.UserID,
      page: 1,
      pageSize: 99999,
      ...query,
    });
    const list = res
      .map((item) => {
        return {
          ...item,
          memberList: item.memberList.filter((member) =>
            isValidEmail(member.email)
          ),
        };
      })
      .filter((item) => item.gameName && item.memberList.length);

    setSdkList(list);
    setLastQuery({
      ...lastQuery,
      filter: { ...filter },
      result: [...list],
      allList: [...list],
    });
    setLoading(false);
  };

  const onSave = async () => {
    onChange(lastQuery);
    return true;
  };

  return (
    <ConsoleDrawer
      title={getFormatMessage('notice_add')}
      width={1350}
      onClose={onClose}
      onOk={onSave}
      okButtonDisabled={!lastQuery.result?.length}
    >
      <div className={styles.drawerBody}>
        <FilterGroup
          className={styles.filter}
          values={filter}
          onFilterChange={(values, changeField) => {
            if (changeField === 'type') {
              setFilter({ ...values, searchValues: [] });
              return;
            }
            setFilter(values);
          }}
        >
          <CommonRadioButton
            name="type"
            optionType="button"
            options={[
              {
                label: getFormatMessage('GameID'),
                value: FilterType.GameID,
              },
              {
                label: getFormatMessage('notice_gcloud_sdk_ver'),
                value: FilterType.GcloudVersion,
              },
              {
                label: getFormatMessage('notice_component_ver'),
                value: FilterType.SdkVersion,
              },
            ]}
          />

          {renderSelect()}
          <CommonButton className={styles.clearBtn} onClick={clearForm}>
            <ReloadOutlined />
          </CommonButton>
          <MainButton onClick={search}>
            {getFormatMessage('notice_search_btn')}
          </MainButton>
        </FilterGroup>

        <span>
          {filter.type === FilterType.GameID
            ? getFormatMessage('notice_download_history')
            : getFormatMessage('notice_version_filter')}
        </span>
        <NoticeSDKList
          loading={loading}
          rawList={sdkList}
          onChange={(selectedList, allList) => {
            setLastQuery({ ...lastQuery, result: [...selectedList], allList });
          }}
        />
      </div>
    </ConsoleDrawer>
  );
};
